/** design.md §3 — the three shells. Every screen is one of them. */

import { useQuery } from '@tanstack/react-query'
import {
  Bot,
  Check,
  House,
  ListChecks,
  LifeBuoy,
  LogOut,
  Menu as MenuIcon,
  MessageCircle,
  Monitor,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  Send,
  Settings,
  Sun,
  TrendingUp,
  Users,
} from 'lucide-react'
import * as React from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'

import {
  type AgentContext,
  AgentContextProvider,
  AgentDock,
  openAgent,
} from '@/components/AgentPanel'
import { Avatar } from '@/components/bits'
import { NotificationsBell } from '@/components/NotificationsBell'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sheet,
  Tooltip,
} from '@/components/ui/overlay'
import { api } from '@/lib/api'
import { usePageTitle, useProductName } from '@/lib/title'
import type { AppSummary, Me } from '@/lib/types'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/auth'
import { PreviewBar } from '@/providers/preview'
import { useTheme } from '@/providers/theme'

/** Maps sys_app.icon (a token, not a component) to a real icon. */
const APP_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'trending-up': TrendingUp,
  'life-buoy': LifeBuoy,
  users: Users,
  receipt: Receipt,
  send: Send,
  'message-circle': MessageCircle,
}

export function appIcon(name: string) {
  return APP_ICONS[name] ?? House
}

export function useApps() {
  return useQuery({
    queryKey: ['apps'],
    queryFn: () => api.get<AppSummary[]>('/api/apps'),
    staleTime: 5 * 60_000,
  })
}

/** An app's name for breadcrumbs and messages, never its slug. */
export function useAppName(slug: string): string {
  return useApps().data?.find((app) => app.slug === slug)?.name ?? slug
}

/** The admin screens, each gated on a real permission (design.md §P9). */
export const ADMIN_NAV = [
  { to: '/admin/users', label: 'Users', permission: 'system:users:manage' },
  { to: '/admin/access', label: 'Access levels', permission: 'system:users:manage' },
  { to: '/admin/organisation', label: 'Organisation', permission: 'system:organisation:manage' },
  { to: '/admin/schema', label: 'Schema', permission: 'system:schema:manage' },
  { to: '/admin/apps', label: 'Apps', permission: 'system:apps:manage' },
  {
    to: '/admin/integrations',
    label: 'Integrations',
    permission: 'system:integrations:manage',
  },
  { to: '/admin/automation', label: 'Automation', permission: 'system:audit:read' },
  { to: '/admin/audit', label: 'Audit log', permission: 'system:audit:read' },
  { to: '/admin/assistant-usage', label: 'Assistant usage', permission: 'system:audit:read' },
  { to: '/admin/system', label: 'System', permission: 'system:audit:read' },
  { to: '/admin/login-logs', label: 'Login logs', permission: 'system:login_logs:read' },
] as const

// --- top bar ----------------------------------------------------------------------

function ThemeMenu() {
  const { theme, setTheme } = useTheme()
  const options = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ] as const
  return (
    <>
      <DropdownMenuLabel>Theme</DropdownMenuLabel>
      {options.map(({ value, label, icon: Icon }) => (
        <DropdownMenuItem key={value} onSelect={() => setTheme(value)}>
          <Icon />
          <span className="flex-1">{label}</span>
          {theme === value ? <Check className="size-3.5 text-accent" /> : null}
        </DropdownMenuItem>
      ))}
    </>
  )
}

const LEVEL_NAME = { member: 'Member', manager: 'Manager', admin: 'Admin' } as const

/** "‹App› Manager · ‹App› Admin": what the signed-in person is, in the apps they have. */
function accessLine(me: Me, apps: AppSummary[] | undefined): string {
  const parts = Object.entries(me.levels).map(
    ([slug, level]) =>
      `${apps?.find((app) => app.slug === slug)?.name ?? slug} ${LEVEL_NAME[level]}`,
  )
  if (me.is_superuser) parts.unshift('Super admin')
  else if (me.platform_admin) parts.unshift('Platform admin')
  return parts.length > 0 ? parts.join(' · ') : 'No access yet'
}

const SIDEBAR_KEY = 'sbt.sidebar.open'
/** design.md §3: from here the sidebar sits beside the content; below, it is a drawer. */
const SIDEBAR_WIDE = '(min-width: 1100px)'

function useMedia(query: string): boolean {
  const [matches, setMatches] = React.useState(() => window.matchMedia(query).matches)
  React.useEffect(() => {
    const media = window.matchMedia(query)
    const onChange = () => setMatches(media.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [query])
  return matches
}

// The sidebar's state outlives a page: every page draws its own shell, and choosing an
// app (the rail, the bottom bar, Home) asks for the panel before the app's page exists.
const sidebarState = {
  docked: (() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) !== 'false'
    } catch {
      return true
    }
  })(),
  drawer: false,
}
const sidebarListeners = new Set<() => void>()
let sidebarSnapshot = { ...sidebarState }

function setSidebar(next: Partial<typeof sidebarState>) {
  Object.assign(sidebarState, next)
  if ('docked' in next) {
    try {
      localStorage.setItem(SIDEBAR_KEY, String(sidebarState.docked))
    } catch {
      // Remembering is a convenience; the sidebar still opens and closes.
    }
  }
  sidebarSnapshot = { ...sidebarState }
  sidebarListeners.forEach((listener) => listener())
}

/** design.md §3: choosing an app shows its sidebar beside the content when there is
 *  room, so its pages are in view. Narrower, the app opens alone: a drawer over a
 *  page just chosen is in the way, and Menu is one tap off. Call it from the link
 *  that opens the app. */
export function openAppMenu() {
  if (window.matchMedia(SIDEBAR_WIDE).matches) setSidebar({ docked: true })
}

/** A link chosen inside the drawer closes it: that is choosing a page. Nothing else
 *  that moves the address (an app going on to its dashboard, a period written into
 *  it) does. */
function closeOnLink(event: React.MouseEvent) {
  if ((event.target as HTMLElement).closest('a[href]')) setSidebar({ drawer: false })
}

/**
 * design.md §3 — the sidebar, shown or hidden from its own header. Wide, it sits beside
 * the content and the choice is remembered (per browser, a convenience). Narrower, it
 * starts closed and opens over the content as a drawer; choosing a page closes it.
 */
function useSidebar() {
  const wide = useMedia(SIDEBAR_WIDE)
  const state = React.useSyncExternalStore(
    (listener) => {
      sidebarListeners.add(listener)
      return () => sidebarListeners.delete(listener)
    },
    () => sidebarSnapshot,
  )
  const setDrawer = React.useCallback((open: boolean) => setSidebar({ drawer: open }), [])
  const toggle = React.useCallback(() => {
    if (wide) setSidebar({ docked: !sidebarState.docked })
    else setSidebar({ drawer: !sidebarState.drawer })
  }, [wide])
  return {
    wide,
    docked: wide && state.docked,
    drawer: !wide && state.drawer,
    setDrawer,
    toggle,
  }
}

/** The sidebar's own header, as the agent panel has: what it is — the app's name — and
 *  the button that closes it. */
function SidebarHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <header className="flex items-center justify-between gap-2 border-b border-border-soft py-2 pr-2 pl-4">
      <p className="truncate text-[13px] font-semibold" title={title}>
        {title}
      </p>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={onClose}
        aria-label="Hide menu"
        title="Hide menu"
      >
        <PanelLeftClose />
      </Button>
    </header>
  )
}

/** A closed sidebar leaves a strip at its edge with the button that opens it, as the
 *  agent panel leaves its tab. */
function SidebarTab({ onOpen }: { onOpen: () => void }) {
  return (
    <div className="hidden shrink-0 border-r border-border bg-surface md:block print:hidden">
      <Button
        variant="ghost"
        size="icon"
        onClick={onOpen}
        aria-label="Show menu"
        title="Show menu"
        className="m-1"
      >
        <PanelLeftOpen />
      </Button>
    </div>
  )
}

/** Where the page sits, general to specific: the breadcrumb and the tab title. */
function TopBar({ trail = [] }: { trail?: readonly string[] }) {
  const { me, logout } = useAuth()
  const apps = useApps()
  const product = useProductName()
  const navigate = useNavigate()
  usePageTitle(trail)
  if (!me) return null

  return (
    <header
      className="flex shrink-0 items-center gap-3 border-b border-border bg-surface px-4 print:hidden"
      style={{ height: 'var(--topbar-h)' }}
    >
      <Link to="/" className="flex shrink-0 items-center gap-2 rounded-md px-1 py-1">
        {/* The same mark as the browser tab (public/favicon.svg). */}
        <img src="/favicon.svg" alt="" className="size-6" />
        {/* On a phone the mark alone: the breadcrumb needs the room. */}
        <span className="hidden text-[13px] font-semibold tracking-tight sm:inline">{product}</span>
      </Link>

      {trail.length > 0 ? (
        <nav aria-label="Breadcrumb" className="min-w-0 truncate text-[13px] text-muted">
          {trail.join(' › ')}
        </nav>
      ) : null}

      <div className="ml-auto flex items-center gap-1">
        <NotificationsBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-surface-2"
              aria-label="Account menu"
            >
              <Avatar name={me.user.name} email={me.user.email} src={me.user.picture_url} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-60">
            <div className="px-2.5 py-2">
              <p className="truncate text-[13px] font-medium">{me.user.name}</p>
              <p className="truncate text-[11px] text-faint">{me.user.email}</p>
              <p className="mt-1.5 text-[11px] text-muted">
                {accessLine(me, apps.data)}
              </p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/approve')}>
              <ListChecks />
              Waiting for you
            </DropdownMenuItem>
            {me.user.connector_access ? (
              <DropdownMenuItem onSelect={() => navigate('/connections')}>
                <Bot />
                Connected assistants
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuSeparator />
            <ThemeMenu />
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void logout()}>
              <LogOut />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}

// --- rail -------------------------------------------------------------------------

function RailLink({
  to,
  label,
  icon: Icon,
  disabled,
  edge = 'left',
  onOpen,
}: {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  disabled?: boolean
  /** Choosing it opens that app's sidebar too (design.md §3). */
  onOpen?: () => void
  /** Where the active bar sits: the rail's left edge, or the bottom bar's top. */
  edge?: 'left' | 'top'
}) {
  const content = (
    <span
      className={cn(
        'relative flex size-10 items-center justify-center rounded-md transition-colors',
        disabled ? 'cursor-not-allowed text-faint/60' : 'text-muted hover:bg-surface-2 hover:text-text',
      )}
    >
      <Icon className="size-[18px]" />
    </span>
  )

  if (disabled) {
    return (
      <Tooltip content={`${label} — not open yet`}>
        <div aria-disabled>{content}</div>
      </Tooltip>
    )
  }

  return (
    <Tooltip content={label}>
      <NavLink to={to} aria-label={label} className="block" onClick={onOpen}>
        {({ isActive }) => (
          <span
            className={cn(
              'relative flex size-10 items-center justify-center rounded-md transition-colors',
              isActive
                ? 'bg-accent-soft text-accent'
                : 'text-muted hover:bg-surface-2 hover:text-text',
            )}
          >
            {/* design.md §3: active app carries a 2px accent bar on its left edge. */}
            {isActive ? (
              <span
                className={cn(
                  'absolute rounded-full bg-accent',
                  edge === 'left'
                    ? 'top-1/2 -left-2 h-5 w-0.5 -translate-y-1/2'
                    : '-top-2 left-1/2 h-0.5 w-5 -translate-x-1/2',
                )}
                aria-hidden
              />
            ) : null}
            <Icon className="size-[18px]" />
          </span>
        )}
      </NavLink>
    </Tooltip>
  )
}

function Rail() {
  const { canAny } = useAuth()
  const apps = useApps()
  const showAdmin = canAny(...ADMIN_NAV.map((item) => item.permission))

  return (
    <nav
      className="hidden shrink-0 flex-col items-center gap-1 border-r border-border bg-surface py-3 md:flex print:hidden"
      style={{ width: 'var(--rail-w)' }}
      aria-label="Apps"
    >
      <RailLink to="/" label="Home" icon={House} />
      {/* Apps, then integrations under their own divider (integrations.md). */}
      {(['app', 'integration'] as const).map((kind) => {
        const listed = (apps.data ?? []).filter((app) => (app.kind ?? 'app') === kind)
        if (listed.length === 0) return null
        return (
          <React.Fragment key={kind}>
            <div className="my-1 h-px w-6 bg-border-soft" />
            {listed.map((app) => (
              <RailLink
                key={app.slug}
                to={`/app/${app.slug}`}
                label={app.name}
                icon={appIcon(app.icon)}
                disabled={app.status !== 'active'}
                onOpen={() => openAppMenu()}
              />
            ))}
          </React.Fragment>
        )
      })}
      {showAdmin ? (
        <>
          <div className="mt-auto h-px w-6 bg-border-soft" />
          <RailLink
            to="/admin/users"
            label="Administration"
            icon={Settings}
            onOpen={() => openAppMenu()}
          />
        </>
      ) : null}
    </nav>
  )
}

/** design.md §3 — below 768 px the rail is a bottom bar: Menu (this app's pages and
 *  tables, as a sheet), Home, the apps, Administration, and the assistant. */
/** Phones: the rail as a bottom bar. Menu opens the shell's drawer (design.md §3). */
function MobileBar({ hasMenu }: { hasMenu: boolean }) {
  const { canAny } = useAuth()
  const apps = useApps()
  const showAdmin = canAny(...ADMIN_NAV.map((item) => item.permission))
  const listed = (apps.data ?? []).filter((app) => app.status === 'active')
  return (
    <>
      <nav
        className="fixed inset-x-0 bottom-0 z-30 flex h-14 items-center gap-1 overflow-x-auto border-t border-border bg-surface px-2 md:hidden print:hidden"
        aria-label="Apps"
      >
        {hasMenu ? (
          <Button variant="ghost" size="icon" aria-label="Menu" onClick={() => setSidebar({ drawer: true })}>
            <MenuIcon />
          </Button>
        ) : null}
        <RailLink to="/" label="Home" icon={House} edge="top" />
        {listed.map((app) => (
          <RailLink
            key={app.slug}
            to={`/app/${app.slug}`}
            label={app.name}
            icon={appIcon(app.icon)}
            edge="top"
            onOpen={() => openAppMenu()}
          />
        ))}
        {showAdmin ? (
          <RailLink
            to="/admin/users"
            label="Administration"
            icon={Settings}
            edge="top"
            onOpen={() => openAppMenu()}
          />
        ) : null}
        <Button variant="ghost" size="icon" aria-label="Assistant" className="ml-auto" onClick={openAgent}>
          <Bot />
        </Button>
      </nav>
    </>
  )
}

// --- shells -----------------------------------------------------------------------

/** Shell A — Home. Top bar plus content. No rail: home is a destination. */
export function HomeShell({ children }: { children: React.ReactNode }) {
  // No rail here -- home is a destination, not a frame (design.md §3). The agent
  // panel still appears: §P4 requires it on every screen.
  return (
    <AgentContextProvider
      value={{
        label: 'Home',
        hints: ['What can I ask here?', 'Add a field to a table'],
      }}
    >
      <div className="flex h-full flex-col">
        <TopBar />
        <div className="flex min-h-0 flex-1">
          <main className="relative min-w-0 flex-1 overflow-y-auto pb-14 md:pb-0">{children}</main>
          <AgentDock />
        </div>
        <MobileBar hasMenu={false} />
      </div>
    </AgentContextProvider>
  )
}

/**
 * Shells B and C — workspace. Rail, optional sidebar, content, agent panel.
 * Below 1440px the agent panel is hidden rather than squeezing the content;
 * the sheet variant lands with the functional agent in Phase 4 (design.md §3).
 */
export function WorkspaceShell({
  sidebar,
  trail,
  agentContext,
  children,
}: {
  sidebar?: React.ReactNode
  /** design.md §3: where this page sits, general to specific (`Billing`, `Payments`,
   *  a record's name). The top bar shows it as the breadcrumb, the browser tab
   *  reversed as the title. */
  trail?: readonly string[]
  /**
   * design.md §P4 makes the agent's context header mandatory and requires it to
   * be accurate. Making it a required prop is how that rule is enforced: a new
   * screen cannot compile without stating what "this" means.
   */
  agentContext: AgentContext
  children: React.ReactNode
}) {
  const menu = useSidebar()
  // The panel is named after where it is: the app, or Administration.
  const menuTitle = trail?.[0] ?? 'Menu'

  return (
    <AgentContextProvider value={agentContext}>
    <div className="flex h-full flex-col print:block print:h-auto">
      <TopBar trail={trail} />
      <div className="flex min-h-0 flex-1 print:block">
        <Rail />
        {sidebar && menu.docked ? (
          // The column is gone when its nav renders nothing -- design.md §P9: the
          // section is absent, not an empty labelled box.
          <div
            className="hidden shrink-0 flex-col border-r border-border bg-surface md:flex print:hidden [&:has(>[data-sidebar-body]:empty)]:hidden"
            style={{ width: 'var(--sidebar-w)' }}
          >
            <SidebarHeader title={menuTitle} onClose={menu.toggle} />
            <div data-sidebar-body className="min-h-0 flex-1 overflow-y-auto">
              {sidebar}
            </div>
          </div>
        ) : null}
        {sidebar && !menu.docked && !menu.drawer ? <SidebarTab onOpen={menu.toggle} /> : null}
        {sidebar && !menu.wide ? (
          // Narrower than the docked width: the sidebar opens over the content, as the
          // assistant does — focus stays inside, Escape and the backdrop close it.
          <Dialog open={menu.drawer} onOpenChange={menu.setDrawer}>
            <Sheet
              title={menuTitle}
              className="right-auto left-0 w-[var(--sidebar-w)] border-r border-l-0 print:hidden"
            >
              <SidebarHeader title={menuTitle} onClose={() => menu.setDrawer(false)} />
              <div className="min-h-0 flex-1 overflow-y-auto" onClick={closeOnLink}>
                {sidebar}
              </div>
            </Sheet>
          </Dialog>
        ) : null}

        <main className="relative min-w-0 flex-1 overflow-y-auto pb-14 md:pb-0 print:overflow-visible print:pb-0">
          <div className="mx-auto max-w-[90rem] px-4 py-4 md:px-6 md:py-5 print:max-w-none print:p-0">
            <PreviewBar />
            {children}
          </div>
        </main>

        {/* Beside the content when there is room; a tab and a sheet when not (§3). */}
        <AgentDock />
      </div>
      <MobileBar hasMenu={Boolean(sidebar)} />
    </div>
    </AgentContextProvider>
  )
}

/** Sidebar used by Shell C. */
export function AdminSidebar() {
  const { can } = useAuth()
  const location = useLocation()
  const visible = ADMIN_NAV.filter((item) => can(item.permission))

  // design.md §P9: with none of these permissions the whole section is absent,
  // not an empty box with a heading.
  if (visible.length === 0) return null

  return (
    // The panel's header already says Administration (design.md §3).
    <nav className="py-3" aria-label="Administration">
      <ul className="px-1.5">
        {visible.map((item) => (
          <li key={item.to}>
            <Link
              to={item.to}
              className={cn(
                'flex items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors',
                location.pathname === item.to
                  ? 'bg-accent-soft font-medium text-accent'
                  : 'text-muted hover:bg-surface-2 hover:text-text',
              )}
            >
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  )
}
